/**
 * Showcase — Astryx 컴포넌트 카탈로그 (dev 전용, `/dev/showcase`).
 *
 * 디자이너가 "이 컴포넌트로 바꿔주세요"라고 **이름으로 지시**할 수 있게 하는 것이 목적이다.
 * 각 항목에 컴포넌트 이름과 import 경로를 그대로 노출한다.
 *
 * ⚠ 여기에 커스텀 컴포넌트를 추가하지 말 것. Astryx 에 있는 것만 싣는다.
 *   Astryx 전체 목록·props 는 `npx astryx component <이름> --json` 이 정본이다.
 *
 * 관련 문서: DESIGNER.md
 */
import { useState } from "react";

import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { TextInput } from "@astryxdesign/core/TextInput";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { RadioList, RadioListItem } from "@astryxdesign/core/RadioList";
import { Switch } from "@astryxdesign/core/Switch";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "@astryxdesign/core/SegmentedControl";
import { Banner } from "@astryxdesign/core/Banner";
import { Spinner } from "@astryxdesign/core/Spinner";
import { ProgressBar } from "@astryxdesign/core/ProgressBar";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Text } from "@astryxdesign/core/Text";
import { Heading } from "@astryxdesign/core/Heading";
import { Badge } from "@astryxdesign/core/Badge";
import { Avatar } from "@astryxdesign/core/Avatar";
import { Divider } from "@astryxdesign/core/Divider";
import { Card } from "@astryxdesign/core/Card";
import Icon from "../components/primitives/Icon";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import { Grid } from "@astryxdesign/core/Grid";

import NativeTest from "./NativeTest";
import { isNative, getPlatform } from "../native";

/** 한 컴포넌트 데모 — 이름 + import 경로를 그대로 보여준다. */
function Demo({
  name,
  from,
  note,
  children,
}: {
  name: string;
  from: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <Card padding={3}>
      <VStack gap={2}>
        <VStack gap={0.5}>
          <Text type="label">{name}</Text>
          <Text type="code" color="secondary">
            {`import { ${name} } from "${from}"`}
          </Text>
          {note && (
            <Text type="supporting" color="secondary">
              {note}
            </Text>
          )}
        </VStack>
        <Divider />
        {children}
      </VStack>
    </Card>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <VStack gap={3}>
      <Heading level={2}>{title}</Heading>
      <VStack gap={2}>{children}</VStack>
    </VStack>
  );
}

export default function Showcase() {
  const [text, setText] = useState("");
  const [checked, setChecked] = useState(true);
  const [radio, setRadio] = useState("a");
  const [toggle, setToggle] = useState(true);
  const [segment, setSegment] = useState("all");

  return (
    <VStack gap={5} padding={3} maxWidth={1120}>
      <VStack gap={1}>
        <Heading level={1}>Astryx 컴포넌트 카탈로그</Heading>
        <Text color="secondary">
          peeka 에서 쓰는 Astryx 컴포넌트 모음입니다. 이름으로 지시하세요 — 예: &quot;이 목록을
          Grid 2열로, 각 항목을 Card 로&quot;.
        </Text>
        <Text type="supporting" color="secondary">
          여기 없는 컴포넌트는 `npx astryx search &lt;키워드&gt; --json` 으로 찾을 수 있습니다.
        </Text>
      </VStack>

      {/* ---------------------------------------------------------------- */}
      <Group title="버튼">
        <Demo name="Button" from="@astryxdesign/core/Button" note="label prop 필수 (children 아님)">
          <VStack gap={2}>
            <HStack gap={1} wrap="wrap">
              <Button label="Primary" variant="primary" />
              <Button label="Secondary" variant="secondary" />
              <Button label="Ghost" variant="ghost" />
              <Button label="Destructive" variant="destructive" />
            </HStack>
            <HStack gap={1} wrap="wrap" align="center">
              <Button label="Small" size="sm" />
              <Button label="Medium" size="md" />
              <Button label="Large" size="lg" />
            </HStack>
            <HStack gap={1} wrap="wrap">
              <Button label="Disabled" isDisabled />
              <Button label="Loading" isLoading />
              <Button label="아이콘" icon={<Icon name="Camera" size="md" />} />
              <Button label="후행" endContent={<Icon name="arrowRight" size="md" />} />
            </HStack>
            <Button label="전체 폭 (width='100%')" variant="primary" width="100%" />
          </VStack>
        </Demo>

        <Demo name="IconButton" from="@astryxdesign/core/IconButton" note="아이콘 전용 버튼">
          <HStack gap={1}>
            <IconButton label="삭제" icon={<Icon name="Delete" size="md" />} />
            <IconButton label="닫기" icon={<Icon name="Close" size="md" />} variant="ghost" />
          </HStack>
        </Demo>
      </Group>

      {/* ---------------------------------------------------------------- */}
      <Group title="입력">
        <Demo
          name="TextInput"
          from="@astryxdesign/core/TextInput"
          note="autoComplete/maxLength 는 타입에 없음 — primitives/TextField 래퍼 사용"
        >
          <VStack gap={2}>
            <TextInput label="기본" value={text} onChange={setText} placeholder="입력해 주세요" />
            <TextInput label="필수" value="" onChange={() => {}} isRequired />
            <TextInput
              label="설명 있음"
              value=""
              onChange={() => {}}
              description="8자 이상, 영문+숫자+특수문자"
            />
            <TextInput
              label="에러"
              value="abc"
              onChange={() => {}}
              status={{ type: "error", message: "형식이 올바르지 않아요" }}
            />
            <TextInput label="비활성" value="" onChange={() => {}} isDisabled />
            <TextInput label="지우기 버튼" value="지울 수 있어요" onChange={() => {}} hasClear />
          </VStack>
        </Demo>
      </Group>

      {/* ---------------------------------------------------------------- */}
      <Group title="선택">
        <Demo name="CheckboxInput" from="@astryxdesign/core/CheckboxInput">
          <VStack gap={1}>
            <CheckboxInput label="동의합니다" value={checked} onChange={setChecked} />
            <CheckboxInput
              label="설명이 있는 항목"
              description="부가 설명이 라벨 아래에 붙습니다"
              value={false}
              onChange={() => {}}
            />
            <CheckboxInput label="비활성" value={false} onChange={() => {}} isDisabled />
          </VStack>
        </Demo>

        <Demo name="RadioList" from="@astryxdesign/core/RadioList" note="RadioListItem 과 함께 사용">
          <RadioList label="선택지" value={radio} onChange={setRadio}>
            <RadioListItem value="a" label="첫 번째" />
            <RadioListItem value="b" label="두 번째" description="설명을 붙일 수 있습니다" />
            <RadioListItem value="c" label="비활성" isDisabled />
          </RadioList>
        </Demo>

        <Demo name="Switch" from="@astryxdesign/core/Switch" note="즉시 반영되는 설정에 사용">
          <VStack gap={1}>
            <Switch label="알림 받기" value={toggle} onChange={setToggle} />
            <Switch
              label="라벨 양끝 배치"
              labelSpacing="spread"
              value={false}
              onChange={() => {}}
            />
          </VStack>
        </Demo>

        <Demo name="SegmentedControl" from="@astryxdesign/core/SegmentedControl">
          <SegmentedControl label="필터" value={segment} onChange={setSegment}>
            <SegmentedControlItem value="all" label="전체" />
            <SegmentedControlItem value="mine" label="내 카드" />
            <SegmentedControlItem value="wish" label="위시" />
          </SegmentedControl>
        </Demo>
      </Group>

      {/* ---------------------------------------------------------------- */}
      <Group title="상태 · 피드백">
        <Demo name="Banner" from="@astryxdesign/core/Banner">
          <VStack gap={1}>
            <Banner status="info" title="안내" description="정보성 메시지입니다." />
            <Banner status="success" title="완료" />
            <Banner status="warning" title="주의" />
            <Banner status="error" title="오류" description="다시 시도해 주세요." />
          </VStack>
        </Demo>

        <Demo name="Spinner / ProgressBar" from="@astryxdesign/core/Spinner">
          <VStack gap={2}>
            <HStack gap={2} align="center">
              <Spinner size="sm" />
              <Spinner size="md" />
            </HStack>
            <ProgressBar label="진행률" value={60} hasValueLabel />
            <ProgressBar label="불확정" isIndeterminate />
          </VStack>
        </Demo>

        <Demo name="Skeleton" from="@astryxdesign/core/Skeleton" note="index 로 stagger 지연">
          <VStack gap={1}>
            <Skeleton width="100%" height={20} />
            <Skeleton width="70%" height={20} index={1} />
            <Skeleton width="40%" height={20} index={2} />
          </VStack>
        </Demo>

        <Demo name="EmptyState" from="@astryxdesign/core/EmptyState">
          <EmptyState
            title="아직 카드가 없어요"
            description="스캔해서 첫 카드를 추가해 보세요."
            actions={<Button label="스캔하기" variant="primary" />}
          />
        </Demo>
      </Group>

      {/* ---------------------------------------------------------------- */}
      <Group title="표시">
        <Demo name="Text / Heading" from="@astryxdesign/core/Text">
          <VStack gap={1}>
            <Heading level={1}>Heading level 1</Heading>
            <Heading level={3}>Heading level 3</Heading>
            <Text type="large">large — 강조 본문</Text>
            <Text>body — 기본 본문</Text>
            <Text type="label">label — 라벨</Text>
            <Text type="supporting" color="secondary">
              supporting — 보조 설명
            </Text>
            <Text type="code">code — 코드</Text>
            <Text type="supporting" color="accent">
              ⚠ display-1~3 은 블랙레터입니다. 금액·수치에 쓰지 마세요.
            </Text>
          </VStack>
        </Demo>

        <Demo name="Badge / Avatar" from="@astryxdesign/core/Badge">
          <HStack gap={2} align="center" wrap="wrap">
            <Badge label="NEW" />
            <Badge label="희귀" variant="success" />
            <Badge label="품절" variant="error" />
            <Avatar name="김문기" size="sm" />
            <Avatar name="김문기" />
          </HStack>
        </Demo>

        <Demo name="Card" from="@astryxdesign/core/Card" note="padding 은 spacing 스케일 숫자">
          <Grid columns={2} gap={2}>
            <Card padding={3}>
              <Text>기본 Card</Text>
            </Card>
            <Card padding={3} variant="muted">
              <Text>variant=&quot;muted&quot;</Text>
            </Card>
          </Grid>
        </Demo>
      </Group>

      {/* ---------------------------------------------------------------- */}
      <Group title="레이아웃">
        <Demo
          name="VStack / HStack / Grid"
          from="@astryxdesign/core/Layout"
          note="레이아웃은 이 컨테이너로만. raw div + flex 금지."
        >
          <VStack gap={2}>
            <Text type="label">VStack gap={"{2}"}</Text>
            <VStack gap={2}>
              <Card padding={2}>
                <Text>1</Text>
              </Card>
              <Card padding={2}>
                <Text>2</Text>
              </Card>
            </VStack>

            <Text type="label">HStack gap={"{1}"}</Text>
            <HStack gap={1}>
              <Card padding={2}>
                <Text>A</Text>
              </Card>
              <Card padding={2}>
                <Text>B</Text>
              </Card>
              <Card padding={2}>
                <Text>C</Text>
              </Card>
            </HStack>

            <Text type="label">Grid columns={"{3}"}</Text>
            <Grid columns={3} gap={1}>
              <Card padding={2}>
                <Text>1</Text>
              </Card>
              <Card padding={2}>
                <Text>2</Text>
              </Card>
              <Card padding={2}>
                <Text>3</Text>
              </Card>
            </Grid>
          </VStack>
        </Demo>
      </Group>

      {/* ---------------------------------------------------------------- */}
      <Group title="네이티브 브리지 (dev)">
        <Demo name="NativeTest" from="./NativeTest" note={`platform: ${getPlatform()} / native: ${isNative()}`}>
          <NativeTest />
        </Demo>
      </Group>
    </VStack>
  );
}
